package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.sizeIn
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.layout.ContentScale
import coil3.compose.AsyncImage
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource

/** Strength of the brand-teal wash toward the bottom of a no-photo cover. Subtle by design. */
private const val CoverWashAlpha = 0.22f

/**
 * On-brand placeholder shown in a business cover area when there is no real photo (all seeded/imported
 * listings until an owner uploads media — see `docs/LOCALIZATION.md`/seed notes). Renders the
 * illustrated category emblem ([placeholderImageRes], see [categoryImageRes]) centered on a soft
 * category-chip field with a gentle brand-teal wash; the emblem is size-capped so it reads as a
 * centered motif on large surfaces (e.g. the tall detail hero) instead of a blown-up full-bleed emoji.
 * Falls back to a centered Material Symbols glyph when no illustration exists. When [imageUrl] is
 * present, the photo is painted over the placeholder so loading/error states still look on-brand.
 */
@Composable
fun BusinessCover(
    modifier: Modifier = Modifier,
    imageUrl: String? = null,
    contentDescription: String? = null,
    iconName: String = "storefront",
    placeholderImageRes: DrawableResource? = null,
    /**
     * Fit a tall photo inside the frame instead of cropping it (QA 2026-09-12: a business header is
     * 16:10, so a portrait — and this directory is full of them — was cropped straight through the
     * subject's face). Wide photos still crop to fill, which is what a header wants. Off by default:
     * a small card thumbnail reads better cropped.
     */
    fitTallImages: Boolean = false,
) {
    Box(
        modifier = modifier
            .fillMaxSize()
            .background(AppTheme.colors.categoryChip),
        contentAlignment = Alignment.Center,
    ) {
        // Subtle brand-teal wash, deepening toward the bottom, composited over the chip field.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(
                    Brush.verticalGradient(
                        listOf(Color.Transparent, AppTheme.colors.primaryContainer.copy(alpha = CoverWashAlpha)),
                    ),
                ),
        )
        if (placeholderImageRes != null) {
            // Cap the emblem so it never fills a large cover; on small thumbnails the box is already
            // below the cap, so it scales down naturally. Fit + padding keeps it tidy at every size.
            Image(
                painter = painterResource(placeholderImageRes),
                contentDescription = null,
                contentScale = ContentScale.Fit,
                modifier = Modifier
                    .sizeIn(maxWidth = AppTheme.sizes.coverMotifMax, maxHeight = AppTheme.sizes.coverMotifMax)
                    .fillMaxSize()
                    .padding(AppTheme.spacing.md),
            )
        } else {
            AppIcon(
                name = iconName,
                size = AppTheme.sizes.iconHero,
                tint = AppTheme.colors.primary,
            )
        }
        if (!imageUrl.isNullOrBlank()) {
            // Decided from the loaded bitmap, not guessed: only a portrait switches to Fit, and it
            // lands on the category-tinted ground already painted behind it.
            var scale by remember(imageUrl) { mutableStateOf(ContentScale.Crop) }
            AsyncImage(
                model = imageUrl,
                contentDescription = contentDescription,
                contentScale = scale,
                onSuccess = { success ->
                    if (fitTallImages) {
                        val image = success.result.image
                        if (image.height > image.width * PORTRAIT_RATIO) scale = ContentScale.Fit
                    }
                },
                modifier = Modifier.fillMaxSize(),
            )
        }
    }
}

/** Taller than this (height ÷ width) counts as a portrait and is fitted rather than cropped. */
private const val PORTRAIT_RATIO = 1.15f
